import React, { useState, useEffect } from 'react';
import { Table, Button, Alert, Popconfirm, Tag } from 'antd';
import { PlusOutlined, StopOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import useTranslation from '@hooks/useTranslation';
import { OrganizationAPIKeyEntity } from '@kemu-io/kemu-types';
import organizationApi, { ListApiKeysResponse } from '@src/api/organization/organizationApi';
import { useAccountMenuContext } from '../AccountMenuContext';
import styles from './ApiKeys.module.css';
import StyledButton from '@components/form-control/styledButton/styledButton';
import GenerateApiKeyModal from './GenerateApiKeyModal';
import useGlobalNotification from '@hooks/useGlobalNotification';

/**
 * Component for managing organization API keys
 */
const ApiKeys: React.FC = () => {
  const t = useTranslation('AccountMenu.Modal.ApiKeys');
  const tCommon = t.withBaseKey('CommonWords');
  const { showNotification } = useGlobalNotification();
  
  const [loading, setLoading] = useState(false);
  const [apiKeys, setApiKeys] = useState<ListApiKeysResponse>([]);
  const [error, setError] = useState<string | null>(null);
  const [generateModalVisible, setGenerateModalVisible] = useState(false);

  /**
   * Loads API keys from the backend
   */
  const loadApiKeys = async () => {
    setLoading(true);
    setError(null);
    try {
      const keys = await organizationApi.listApiKeys();
      // Sort keys by createdAt in descending order
      const sortedKeys = keys.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setApiKeys(sortedKeys);
    } catch (err) {
      console.error('Failed to load API keys:', err);
      setError(err instanceof Error ? err.message : t('Error.LoadFailed'));
      showNotification({
        type: 'error',
        title: tCommon('Error'),
        message: t('Error.LoadFailed'),
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handles revoking an API key
   */
  const handleRevokeKey = async (keyId: string) => {
    try {
      setLoading(true);
      await organizationApi.revokeApiKey(keyId);
      showNotification({
        type: 'success',
        title: tCommon('Success'),
        message: t('Success.KeyRevoked'),
      });
      await loadApiKeys();
    } catch (err) {
      console.error('Failed to revoke API key:', err);
      showNotification({
        type: 'error',
        title: tCommon('Error'),
        message: t('Error.RevokeFailed'),
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handles deleting an API key
   */
  const handleDeleteKey = async (keyId: string) => {
    try {
      setLoading(true);
      await organizationApi.deleteApiKey(keyId);
      showNotification({
        type: 'success',
        title: tCommon('Success'),
        message: t('Success.KeyDeleted'),
      });
      await loadApiKeys();
    } catch (err) {
      console.error('Failed to delete API key:', err);
      showNotification({
        type: 'error',
        title: tCommon('Error'),
        message: t('Error.DeleteFailed'),
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Formats date for display
   */
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  /**
   * Table columns configuration
   */
  const columns: ColumnsType<ListApiKeysResponse[0]> = [
    {
      title: t('Column.CreatedAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (createdAt: string) => formatDate(createdAt),
    },
    {
      title: t('Column.Key'),
      dataIndex: 'lastFourDigits',
      key: 'lastFourDigits',
      width: 110,
      render: (key: string) => key ? `****${key}` : '-',
    },
    {
      title: t('Column.CreatedBy'),
      dataIndex: 'createdByUserName',
      key: 'createdBy',
      width: 200,
    },
    {
      title: t('Column.Comment'),
      dataIndex: 'comment',
      key: 'comment',
      width: 200,
      render: (comment?: string) => comment || '-',
    },
    {
      title: t('Column.Status'),
      key: 'status',
      width: 100,
      render: (_, record: ListApiKeysResponse[0]) => {
        if (record.revokedAt) {
          return <Tag color="red">{t('Status.Revoked')}</Tag>;
        }
        return <Tag color="green">{t('Status.Active')}</Tag>;
      },
    },
    {
      title: tCommon('Actions'),
      key: 'actions',
      width: 110,
      render: (_, record: ListApiKeysResponse[0]) => {
        if (record.revokedAt) {
          return (
            <Popconfirm
              title={t('DeleteConfirm.Title')}
              description={t('DeleteConfirm.Description')}
              onConfirm={() => handleDeleteKey(record.id)}
              okText={tCommon('Yes')}
              cancelText={tCommon('No')}
              okType="danger"
            >
              <Button
                type="text"
                danger
                size="small"
                icon={<DeleteOutlined />}
              >
                {t('Actions.Delete')}
              </Button>
            </Popconfirm>
          );
        }
        return (
          <Popconfirm
            title={t('RevokeConfirm.Title')}
            description={t('RevokeConfirm.Description')}
            onConfirm={() => handleRevokeKey(record.id)}
            okText={tCommon('Yes')}
            cancelText={tCommon('No')}
            okType="danger"
          >
            <Button
              type="text"
              danger
              size="small"
              icon={<StopOutlined />}
            >
              {t('Actions.Revoke')}
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  // Load data on component mount
  useEffect(() => {
    loadApiKeys();
  }, []);

  /**
   * Handles modal close and refreshes the list
   */
  const handleModalClose = () => {
    setGenerateModalVisible(false);
    loadApiKeys();
  };

  return (
    <div className={styles.Container}>
      {error && (
        <Alert
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          className={styles.ErrorAlert}
        />
      )}
      <p className={styles.Instructions}>{t('Instructions')}</p>
      <div className={styles.Actions}>
        <StyledButton
          color="primary"
          noShadow
          icon={<PlusOutlined />}
          title={t('Actions.GenerateKey')}
          onClick={() => setGenerateModalVisible(true)}
        />
      </div>
      <Table
        columns={columns}
        dataSource={apiKeys}
        pagination={false}
        rowKey="id"
        size="small"
        className={styles.Table}
        loading={loading}
        locale={{
          emptyText: t('NoKeys'),
        }}
        scroll={apiKeys.length > 8 ? { y: 400, x: 600 } : { x: 600 }}
      />
      <GenerateApiKeyModal
        visible={generateModalVisible}
        onClose={handleModalClose}
      />
    </div>
  );
};

export default ApiKeys;

